import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import { listMedia, uploadMedia, deleteMedia, uploadUrl } from '../lib/api';
import { Trash2, Copy, Upload, X, Check } from 'lucide-react';

export default function MediaLibrary({ pickMode = false, onPick, onClose }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [alt, setAlt] = useState('');
  const [copied, setCopied] = useState('');

  const load = async () => {
    try { setItems(await listMedia()); } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast({ title: 'Choose a file first' });
    setBusy(true);
    try {
      await uploadMedia(file, alt);
      toast({ title: 'Image uploaded' });
      setFile(null); setAlt('');
      await load();
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    await deleteMedia(id);
    await load();
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(uploadUrl(url));
      setCopied(url);
      setTimeout(() => setCopied(''), 1500);
    } catch {}
  };

  const pick = (r) => {
    if (onPick) onPick(r.url);
    onClose && onClose();
  };

  return (
    <div className={pickMode ? 'fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4' : ''}>
      <div className={pickMode ? 'bg-white rounded-2xl border border-aqc-line shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto p-6' : 'bg-white border border-aqc-line rounded-2xl p-6'}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-aqc-navy">Media Library</h2>
          {pickMode && <button onClick={onClose} aria-label="close" className="h-8 w-8 flex items-center justify-center rounded-full border border-aqc-line"><X size={16} /></button>}
        </div>
        <form onSubmit={submit} className="flex flex-wrap items-end gap-2 border border-dashed border-aqc-line rounded-xl p-4 bg-aqc-cream/50">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs uppercase tracking-widest text-aqc-muted font-semibold">Upload image</label>
            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs uppercase tracking-widest text-aqc-muted font-semibold">Alt text</label>
            <Input value={alt} onChange={(e) => setAlt(e.target.value)} className="mt-1" placeholder="Describe the image" />
          </div>
          <Button type="submit" disabled={busy} className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white gap-1.5">
            <Upload size={14} /> {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </form>

        {items.length === 0 ? (
          <p className="mt-6 text-sm text-aqc-muted">No images yet. Upload one to get started.</p>
        ) : (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((r) => (
              <div key={r.id} className="group relative border border-aqc-line rounded-xl overflow-hidden bg-aqc-cream">
                <img src={uploadUrl(r.url)} alt={r.alt || ''} className="w-full h-32 object-cover" />
                <div className="p-2">
                  <div className="text-[0.7rem] text-aqc-muted truncate" title={r.filename}>{r.filename}</div>
                  <div className="text-[0.7rem] text-aqc-muted">{Math.round((r.size || 0) / 1024)} KB</div>
                </div>
                <div className="absolute inset-x-2 bottom-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {pickMode ? (
                    <button onClick={() => pick(r)} className="flex-1 bg-aqc-navy text-white text-xs px-2 py-1.5 rounded-md flex items-center justify-center gap-1"><Check size={12} /> Use</button>
                  ) : (
                    <button onClick={() => copy(r.url)} className="flex-1 bg-white/95 text-aqc-navy text-xs px-2 py-1.5 rounded-md flex items-center justify-center gap-1 border border-aqc-line">
                      {copied === r.url ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy URL</>}
                    </button>
                  )}
                  <button onClick={() => del(r.id)} className="bg-white/95 text-aqc-red border border-aqc-line px-2 py-1.5 rounded-md"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
